import type * as CSS from "csstype";
import { Adder, ConditionMap, push } from "..";
import { _brk, _brkk, _rk } from "../core";
import { extractText } from "../extract-text";
import { mod } from "../maths";
import { Template } from "../nubs";
import {
  formatText,
  isTextFormatType,
  padNumber,
  type TextFormatType,
} from "../utils";
import { isDomRelation, type $RelationOrSelector } from "./relation";

export type $Selector =
  | undefined
  | null
  | $Selector[]
  | string
  | string[]
  | $
  | $[]
  | HTMLCollection
  | HTMLCollection[]
  | Element[]
  | Element
  | NodeListOf<Element>;

export type $Iterator<T = void> = (
  element: Element,
  index: number,
  elements: Element[],
) => T;
export type $Iterator$<T = void> = (
  $element: $,
  index: number,
  elements: Element[],
) => T;

export type $Options = {
  parent?: $Selector | Document;
  namespace?: string[];
  previousElements?: Element[];
};

export type $EventType = keyof GlobalEventHandlersEventMap;

export type $Namespaces = `:${string}`;
export type $NamespacedEvent<K extends $EventType = $EventType> =
  | K
  | `${K}${$Namespaces}`;

type $EventData<K extends $EventType = $EventType> = {
  type: K;
  namespaces: string[];
  handler: EventListener;
  capture: boolean;
};

export type $AttributeValue = string | number | boolean;

export type $Attr<TValue = string> = {
  rawValue: string | null;
  value: TValue;
};

export type $Attrs<TValue = string> = {
  rawValue: string[];
  value: TValue;
};

export class $ {
  private _namespace: string[];
  private _elements: Element[] = [];
  private _previousElements: Element[] = [];

  private static readonly data = new WeakMap<
    Element,
    Record<string, unknown>
  >();
  private static readonly events = new WeakMap<
    Element,
    Partial<Record<$EventType, $EventData[]>>
  >();

  data<T>(key: string): T | undefined;
  data<T>(key: string, value: T): $;
  data<T>(key: string, value?: T): T | undefined | $ {
    if (value === undefined) {
      return this.first
        ? ($.data.get(this.first)?.[key] as T | undefined)
        : undefined;
    }

    return this.each((element) => {
      const data = $.data.get(element) ?? {};

      data[key] = value;

      $.data.set(element, data);
    });
  }

  constructor(selector?: $Selector, options: $Options = {}) {
    const { parent, namespace = [] } = options;

    this._namespace = [...namespace];

    if (selector === undefined || selector === null) {
      this._elements = [];
    } else if (typeof selector === "string") {
      if (parent === undefined) {
        this._elements = Array.from(document.querySelectorAll(selector));
      } else if (parent instanceof Document || parent instanceof Element) {
        this._elements = Array.from(parent.querySelectorAll(selector));
      } else if (parent instanceof $) {
        this._elements = parent
          .flatMap((element) => element.querySelectorAll(selector))
          .get();
      } else {
        this._elements = $.make(parent).find(selector).get();
      }
    } else {
      if (parent !== undefined) {
        throw new SyntaxError(
          "Parent is not allowed when selector is not a string",
        );
      }

      if (Array.isArray(selector)) {
        this._elements = selector.flatMap(
          (selector) => $.make(selector)._elements,
        );
      } else if (selector instanceof Element) {
        this._elements = [selector];
      } else if (
        selector instanceof HTMLCollection ||
        selector instanceof NodeList
      ) {
        this._elements = Array.from(selector);
      } else if (selector instanceof $) {
        this._elements = selector.get();
      } else {
        throw new SyntaxError("Invalid selector");
      }
    }
  }

  get isValid(): boolean {
    return this._elements.length > 0;
  }

  static isInput(
    element: Element,
  ): element is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
    return (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement
    );
  }

  _rk(...keywords: string[]) {
    return _rk(...this._namespace, ...keywords);
  }

  _brk(...keywords: string[]) {
    return _brk(...this._namespace, ...keywords);
  }

  _brkk(value: string, ...keywords: string[]) {
    return _brkk(value, ...this._namespace, ...keywords);
  }

  each(callback: $Iterator) {
    this._elements.forEach(callback);

    return this;
  }

  each$(callback: $Iterator$, withNamespace: boolean = true) {
    const $context = withNamespace ? this : $;

    return this.each((element, index, elements) =>
      callback($context.make(element), index, elements),
    );
  }

  filter(selector: string | Node): $;
  filter(callback: $Iterator<boolean>): $;
  filter(selectorOrCallback: string | Node | $Iterator<boolean>): $ {
    if (typeof selectorOrCallback === "function") {
      return this.make(this._elements.filter(selectorOrCallback));
    }

    return this.make(this._elements).filter$(($element) =>
      $element.is(selectorOrCallback),
    );
  }

  filter$(callback: $Iterator$<boolean>, withNamespace: boolean = true): $ {
    const $context = withNamespace ? this : $;

    return this.make(
      this._elements.filter((element, index, elements) =>
        callback($context.make(element), index, elements),
      ),
    );
  }

  map<T>(callback: $Iterator<T>): T[] {
    return this._elements.map(callback);
  }

  map$<T>(callback: $Iterator$<T>, withNamespace: boolean = true): T[] {
    const $context = withNamespace ? this : $;

    return this.map((element, index, elements) =>
      callback($context.make(element), index, elements),
    );
  }

  reduce<T>(
    callback: (
      previousValue: T,
      element: Element,
      index: number,
      elements: Element[],
    ) => T,
    initialValue: T,
  ): T {
    return this._elements.reduce(
      (previousValue, element, index, elements) =>
        callback(previousValue, element, index, elements),
      initialValue,
    );
  }

  reduce$<T>(
    callback: (
      previousValue: T,
      $element: $,
      index: number,
      elements: Element[],
    ) => T,
    initialValue: T,
    withNamespace: boolean = true,
  ): T {
    const $context = withNamespace ? this : $;

    return this.reduce(
      (previousValue, element, index, elements) =>
        callback(previousValue, $context.make(element), index, elements),
      initialValue,
    );
  }

  setSearchParams(...params: (readonly [string, string | null])[]) {
    this.filter("a").each$(($element) => {
      const href = $element.attr("href") || "";

      const url = new URL(href, window.location.href);

      for (const [key, value] of params) {
        if (value === null) {
          url.searchParams.delete(key);
        } else {
          url.searchParams.set(key, value);
        }
      }

      $element.attr("href", url.toString());
    });
  }

  some(callback: $Iterator<boolean>): boolean {
    return this._elements.some(callback);
  }

  some$(callback: $Iterator$<boolean>, withNamespace: boolean = true): boolean {
    const $context = withNamespace ? this : $;

    return this.some((element, index, elements) =>
      callback($context.make(element), index, elements),
    );
  }

  every(callback: $Iterator<boolean>): boolean {
    return this._elements.every(callback);
  }

  every$(
    callback: $Iterator$<boolean>,
    withNamespace: boolean = true,
  ): boolean {
    const $context = withNamespace ? this : $;

    return this.every((element, index, elements) =>
      callback($context.make(element), index, elements),
    );
  }

  flatMap(callback: $Iterator<$Selector>): $ {
    const results: $Selector[] = [];

    this.each((element, index, elements) => {
      results.push(callback(element, index, elements));
    });

    return this.make(results);
  }

  flatMap$(callback: $Iterator$<$Selector>, withNamespace: boolean = true): $ {
    const $context = withNamespace ? this : $;

    return this.flatMap((element, index, elements) =>
      callback($context.make(element), index, elements),
    );
  }

  groupBy(callback: $Iterator<string>): Record<string, Element[]> {
    return this.reduce<Record<string, Element[]>>(
      (previousValue, element, index, elements) => {
        const key = callback(element, index, elements);

        previousValue[key] ??= [];
        previousValue[key].push(element);

        return previousValue;
      },
      {},
    );
  }

  groupBy$(
    callback: $Iterator$<string>,
    withNamespace: boolean = true,
  ): Record<string, $> {
    const $context = withNamespace ? this : $;

    return this.reduce$<Record<string, $>>(
      (previousValue, $element, index, elements) => {
        const key = callback($element, index, elements);

        previousValue[key] ??= $context.make();
        previousValue[key].push(...$element._elements);

        return previousValue;
      },
      {},
      true,
    );
  }

  find(selector: string, includeSelf: boolean = false): $ {
    const $base = this.make(selector, this);

    if (includeSelf) {
      return $base.with(this.eq(selector));
    }

    return $base;
  }

  closest(selector: string, includeSelf: boolean = false): $ {
    return this.flatMap$((element) =>
      includeSelf && element.eq(selector).isValid
        ? element
        : element.first?.closest(selector),
    );
  }

  prepend($element: $): $;
  prepend(...elements: Element[]): $;
  prepend(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this.first?.prepend(...first._elements);
    } else {
      this.first?.prepend(first, ...elements);
    }

    return this;
  }

  append($element: $): $;
  append(...elements: Element[]): $;
  append(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this.first?.append(...first._elements);
    } else if (first) {
      this.first?.append(first, ...elements);
    }

    return this;
  }

  before($element: $): $;
  before(...elements: Element[]): $;
  before(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this.first?.before(...first._elements);
    } else if (first) {
      this.first?.before(first, ...elements);
    }

    return this;
  }

  after($element: $): $;
  after(...elements: Element[]): $;
  after(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this.first?.after(...first._elements);
    } else if (first) {
      this.first?.after(first, ...elements);
    }

    return this;
  }

  get first(): Element | null {
    return this._elements.at(0) ?? null;
  }

  get length(): number {
    return this._elements.length;
  }

  get empty(): boolean {
    return this.length === 0;
  }

  is(selector: string | Node): boolean {
    if (typeof selector === "string") {
      return this.some((element) => element.matches(selector));
    }

    return this.some((element) => element === selector);
  }

  has(selector: string | Node, includeSelf: boolean = false): boolean {
    if (typeof selector === "string") {
      return this.some(
        (element) =>
          !!element.querySelector(selector) ||
          (includeSelf && element.matches(selector)),
      );
    }

    return this.some(
      (element) =>
        element.contains(selector) || (includeSelf && element === selector),
    );
  }

  addBack(selector?: string): $ {
    const $previousElements = this.make(this._previousElements);

    if (selector) {
      return this.with($previousElements.eq(selector));
    }

    return this.with($previousElements);
  }

  get tag(): string | null {
    return this.first?.tagName.toLowerCase() ?? null;
  }

  get value(): string | null {
    return this.first && $.isInput(this.first) ? this.first.value : null;
  }

  set value(value: string) {
    this.each((element) => {
      if ($.isInput(element)) {
        element.value = value;
      }
    });
  }

  static extractText(root: Element, type: "default" | "smart"): string {
    switch (type) {
      case "smart":
        return extractText(root);

      default:
        return root.textContent ?? "";
    }
  }

  get smartText(): string {
    return this.map((element) => $.extractText(element, "smart")).join(" ");
  }

  get text(): string {
    return this.map((element) => $.extractText(element, "default")).join(" ");
  }

  set text(value: string) {
    this.each((element) => {
      element.textContent = value;
    });
  }

  on<E extends $EventType>(
    event: $NamespacedEvent<E>,
    handler: (event: GlobalEventHandlersEventMap[E]) => void,
    options?: boolean | AddEventListenerOptions,
  ): $ {
    return this.each((element) => {
      const events =
        $.events.get(element) ?? ({} as Record<$EventType, $EventData[]>);
      $.events.set(element, events);

      const [type, ...rest] = event.split(":") as [$EventType, ...string[]];
      const namespaces = rest
        .map((value) => value.trim())
        .filter(Boolean)
        .sort();

      element.addEventListener(type, handler as EventListener, options);

      const typeEvents = (events[type] = events[type] ?? []);

      const capture =
        typeof options === "boolean" ? options : !!options?.capture;

      typeEvents.push({
        type,
        namespaces,
        handler: handler as EventListener,
        capture,
      });
    });
  }

  off(event: $Namespaces | $NamespacedEvent): $ {
    this.each((element) => {
      const events =
        $.events.get(element) ?? ({} as Record<$EventType, $EventData[]>);

      const [type, ...rest] = event.split(":") as [
        $EventType | "",
        ...string[],
      ];
      const namespaces = rest
        .map((value) => value.trim())
        .filter(Boolean)
        .sort();

      Object.keys(events).forEach((_eventType) => {
        const eventType = _eventType as $EventType;

        if (!events[eventType]) {
          return;
        }

        if (type !== "" && type !== eventType) {
          return;
        }

        events[eventType] = events[eventType].filter((event) => {
          const isMatch = namespaces.every((namespace) =>
            event.namespaces.includes(namespace),
          );

          if (isMatch) {
            element.removeEventListener(
              event.type,
              event.handler,
              event.capture,
            );
          }

          return !isMatch;
        });

        if (events[eventType].length === 0) {
          delete events[eventType];
        }
      });
    });
    return this;
  }

  trigger(event: $EventType) {
    // TODO : with namespaces
    this.each((element) => {
      element.dispatchEvent(new Event(event));
    });

    return this;
  }

  blur(): $ {
    return this.each((element) => {
      if (element instanceof HTMLElement) {
        element.blur();
      }
    });
  }

  css<K extends keyof CSS.Properties>(name: K, value: CSS.Properties[K]): this;
  css(
    styles: Partial<{ [P in keyof CSS.Properties]: CSS.Properties[P] }>,
  ): this;
  css(nameOrStyles: any, value?: any): this {
    const entries =
      typeof nameOrStyles === "string"
        ? [nameOrStyles, value]
        : Object.entries(nameOrStyles);

    return this.each((element) => {
      entries.forEach(([name, value]) => {
        if (element instanceof HTMLElement) {
          element.style.setProperty(name, value);
        }
      });
    });
  }

  attr(name: string): string | null;
  attr(name: string, value: $AttributeValue | null): $;
  attr(name: string, value?: $AttributeValue | null): string | null | $ {
    if (value === undefined) {
      return this.first?.getAttribute(name) ?? null;
    } else {
      this.each((element) =>
        value !== null
          ? element.setAttribute(name, value.toString())
          : element.removeAttribute(name),
      );

      return this;
    }
  }

  rk(keyword: string): string | null;
  rk(keyword: string, value: $AttributeValue | null): $;
  rk(keyword: string, value?: $AttributeValue | null): string | null | $ {
    if (value === undefined) {
      return this.attr(this._rk(keyword));
    }

    return this.attr(this._rk(keyword), value);
  }

  rks(
    keyword: string,
    options: {
      separator?: string;
      defaultOnEmpty?: boolean;
      defaultValue?: string[];
    } = {},
  ): string[] {
    const {
      separator = ";",
      defaultOnEmpty = false,
      defaultValue = [],
    } = options;

    const values = this.rk(keyword)
      ?.split(separator)
      .map((value) => value.trim());

    if (!values || (defaultOnEmpty && values.length === 0)) {
      return defaultValue;
    }

    return values;
  }

  string<T extends string | undefined>(
    keyword: string,
    options: { defaultValue?: T; validate?: (value: string) => boolean } = {},
  ): $Attr<T extends undefined ? string | null : string> {
    const { defaultValue = null, validate = () => true } = options;

    const rawValue = this.rk(keyword);
    const value = rawValue ? rawValue.trim() : null;

    if (value === null || !validate(value)) {
      return {
        rawValue,
        value: defaultValue as T extends undefined ? string | null : string,
      };
    }

    return { rawValue, value };
  }

  enum<TValues extends string, T extends TValues | undefined>(
    keyword: string,
    options: { defaultValue?: T; values: TValues[] },
  ): $Attr<T extends undefined ? TValues | null : TValues> {
    const { values, ...rest } = options;

    return this.string(keyword, {
      ...rest,
      validate: (value) => values.includes(value as TValues),
    }) as $Attr<T extends undefined ? TValues | null : TValues>;
  }

  number<T extends number | undefined>(
    keyword: string,
    options: { defaultValue?: T; validate?: (value: number) => boolean } = {},
  ): $Attr<T extends undefined ? number | null : number> {
    const { defaultValue = null, validate = () => true } = options;

    const rawValue = this.rk(keyword);
    const value = rawValue ? Number.parseFloat(rawValue) : NaN;

    if (
      Number.isNaN(value) ||
      !validate(value) ||
      value === Infinity ||
      value === -Infinity
    ) {
      return {
        rawValue,
        value: defaultValue as T extends undefined ? number | null : number,
      };
    }

    return { rawValue, value };
  }

  integer<T extends number | undefined>(
    keyword: string,
    options: { defaultValue?: T; validate?: (value: number) => boolean } = {},
  ): $Attr<T extends undefined ? number | null : number> {
    const { validate = () => true } = options;

    return this.number(keyword, {
      ...options,
      validate: (value) => Number.isInteger(value) && validate(value),
    });
  }

  boolean<T extends boolean | undefined>(
    keyword: string,
    options: { defaultValue?: T } = {},
  ): $Attr<T extends undefined ? boolean | null : boolean> {
    const { defaultValue = null } = options;

    const rawValue = this.rk(keyword);

    if (rawValue === "true") {
      return { rawValue, value: true };
    }

    if (rawValue === "false") {
      return { rawValue, value: false };
    }

    return {
      rawValue,
      value: defaultValue as T extends undefined ? boolean | null : boolean,
    };
  }

  $relatives(relation: $RelationOrSelector): $ {
    if (isDomRelation(relation)) {
      switch (relation) {
        case "grandparent":
          return this.parent.parent;
        case "itself_grandparent":
          return this.parent.parent.with(this);
        case "parent":
          return this.parent;
        case "itself_parent":
          return this.parent.with(this);
        case "itself":
          return this.make(this);
        case "siblings":
          return this.siblings;
        case "itself_siblings":
          return this.siblings.with(this);
        case "children":
          return this.children;
        case "itself_children":
          return this.children.with(this);
        case "descendants":
          return this.find("*");
        case "itself_descendants":
          return this.find("*").with(this);
      }
    } else {
      return this.find(relation).addBack(relation);
    }
  }

  transform(callback: ($element: $) => $): $ {
    return callback(this);
  }

  flaggables(): $Attr<string> {
    return this.string("flag", {
      defaultValue: "itself",
    });
  }

  $flaggables(): $ {
    return this.$relatives(this.flaggables().value);
  }

  class(name: string): boolean;
  class(name: string, value: unknown): $;
  class(name: string, value?: unknown): unknown | $ {
    if (value === undefined) {
      return this.first?.classList.contains(name) ?? false;
    }

    return this.each((element) => {
      element.classList.toggle(name, !!value);
    });
  }

  flag(keyword: string): boolean;
  flag(keyword: string, value: unknown): $;
  flag(keyword: string, value?: unknown): unknown | $ {
    const $targets = this.$flaggables();

    const name = _rk(keyword);

    if (value === undefined) {
      return $targets.class(name);
    }

    return $targets.class(name, value);
  }

  condition(map: ConditionMap, transform?: (condition: string) => string): $ {
    return this.each$(($element) => {
      const conditions = $element.rks("condition");

      if (conditions.length === 0) {
        return;
      }

      map.subscribe({
        tokens: transform
          ? conditions.map((token) => transform(token))
          : conditions,
        callback: (values) => $element.toggle(values.every((value) => value)),
      });
    });
  }

  format(text: string, defaultFormatType: TextFormatType = "none"): $ {
    return this.each$(($element) => {
      const formatType = $element.string("format", {
        defaultValue: defaultFormatType,
        validate: (value) => isTextFormatType(value),
      }).value as TextFormatType;

      this.text = formatText(text, formatType);
    });
  }

  pad(value: number, maxValue: number, padByDefault: boolean = false): $ {
    return this.each$(($element) => {
      const { value: pad } = $element.boolean("pad", {
        defaultValue: padByDefault,
      });

      this.text = pad ? padNumber(value, maxValue) : value.toString();
    });
  }

  toggle(value: unknown): $ {
    /*return this.each((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = value ? "" : "none";
      }
    });*/
    return this.class("hidden", !value);
  }

  get parent(): $ {
    const parents = new Set<Element>();

    this.each((element) => {
      const parent = element.parentElement;

      if (parent) {
        parents.add(parent);
      }
    });

    return this.make(Array.from(parents));
  }

  get children(): $ {
    return this.flatMap((element) => element.children);
  }

  get siblings(): $ {
    return this.flatMap((element) =>
      this.make(element).parent.children.filter((child) => child !== element),
    );
  }

  get next(): $ {
    return this.make(this.first?.nextElementSibling);
  }

  get previous(): $ {
    return this.make(this.first?.previousElementSibling);
  }

  index(): number | null {
    if (!this.first || !this.first.parentElement) {
      return null;
    }

    return Array.from(this.first.parentElement.children).indexOf(this.first);
  }

  static $rk(keyword: string, parent?: $Selector): $ {
    return $.make(_brk(keyword), parent);
  }

  $rk(keyword: string, includeSelf?: boolean): $;
  $rk(includeSelf?: boolean): $;
  $rk(keywordOrIncludeSelf?: string | boolean, includeSelf?: boolean): $ {
    let keyword: string | undefined;

    if (
      typeof keywordOrIncludeSelf === "boolean" ||
      keywordOrIncludeSelf === undefined
    ) {
      includeSelf = keywordOrIncludeSelf;
    } else {
      keyword = keywordOrIncludeSelf;
    }

    const selector = keyword ? this._brk(keyword) : this._brk();

    return this.find(selector, includeSelf);
  }

  static $rkk(
    value: string,
    keyword: string,
    parent?: $Selector | Document,
  ): $ {
    return $.make(_brkk(value, keyword), parent);
  }

  $rkk(value: string, keyword: string, includeSelf?: boolean): $;
  $rkk(value: string, includeSelf?: boolean): $;
  $rkk(
    value: string,
    keywordOrIncludeSelf?: string | boolean,
    includeSelf?: boolean,
  ): $ {
    let keyword: string | undefined;

    if (typeof keywordOrIncludeSelf === "boolean") {
      includeSelf = keywordOrIncludeSelf;
    } else {
      keyword = keywordOrIncludeSelf;
    }

    const selector = keyword ? this._brkk(value, keyword) : this._brkk(value);

    return this.find(selector, includeSelf);
  }

  static $element(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "element"), parent);
  }

  $element(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "element");

    const $base = this.make(selector, this);

    if (includeSelf) {
      return $base.addBack(selector);
    }

    return $base;
  }

  static $condition(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "condition"), parent);
  }

  $condition(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "condition");

    return this.find(selector, includeSelf);
  }

  static $wrap(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "wrap"), parent);
  }

  $wrap(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "wrap");

    return this.find(selector, includeSelf);
  }

  static $text(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "text"), parent);
  }

  $text(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "text");

    return this.find(selector, includeSelf);
  }

  static $button(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "button"), parent);
  }

  $button(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "button");

    return this.find(selector, includeSelf);
  }

  static $input(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "input"), parent);
  }

  $input(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "input");

    const $base = this.make(selector, this);

    if (includeSelf) {
      return $base.addBack(selector);
    }

    return $base;
  }

  static $form(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "form"), parent);
  }

  $form(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "form");

    return this.find(selector, includeSelf);
  }

  static $template(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "template"), parent);
  }

  $template(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "template");

    return this.find(selector, includeSelf);
  }

  static $info(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "info"), parent);
  }

  $info(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "info");

    return this.find(selector, includeSelf);
  }

  static $data(value: string, parent?: $Selector | Document): $ {
    return $.make(_brkk(value, "data"), parent);
  }

  $data(value: string, includeSelf: boolean = false): $ {
    const selector = this._brkk(value, "data");

    return this.find(selector, includeSelf);
  }

  eq(selector: number | Element | string): $ {
    if (typeof selector === "number") {
      return this.filter((_, i) => i === mod(selector, this.length));
    } else if (selector instanceof Element) {
      return this.filter((element) => element === selector);
    } else {
      return this.filter$(($element) => $element.is(selector));
    }
  }

  with($elements: $): $;
  with(...elements: Element[]): $;
  with(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      return this.make(this._elements.concat(first._elements));
    } else {
      return this.make(this._elements.concat(elements));
    }
  }

  push($element: $): $;
  push(...elements: Element[]): $;
  push(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this._elements.push(...first._elements);
    } else if (first) {
      this._elements.push(first, ...elements);
    }

    return this;
  }

  html(): string;
  html(html: string): $;
  html(html?: string): string | $ {
    if (html === undefined) {
      return this.first?.innerHTML ?? "";
    }

    return this.each((element) => {
      element.innerHTML = html;
    });
  }

  get(): Element[];
  get(index: number): Element | null;
  get(index?: number): Element[] | Element | null {
    return index === undefined
      ? [...this._elements]
      : (this._elements.at(index) ?? null);
  }

  replaceChildren($elements: $): $;
  replaceChildren(...elements: Element[]): $;
  replaceChildren(first: $ | Element, ...elements: Element[]): $ {
    if (first instanceof $) {
      this.first?.replaceChildren(...first.get());
    } else {
      this.first?.replaceChildren(first, ...elements);
    }

    return this;
  }

  clone(subtree?: boolean): $ {
    return this.make(
      this.flatMap((element) => element.cloneNode(subtree) as Element),
    );
  }

  resetChildren() {
    return this.each$(($element) => {
      const dataKey = _rk("reset_children");

      const defaultChildrenHtml = $element.data<string>(dataKey);

      if (defaultChildrenHtml) {
        $element.html(defaultChildrenHtml);
      } else {
        $element.data(dataKey, $element.html());
      }
    });
  }

  slice(start?: number, end?: number): $ {
    return this.make(this.get().slice(start, end));
  }

  remove(): $ {
    this.each((element) => {
      element.remove();
    });

    return this;
  }

  static namespace(...namespace: string[]): $ {
    const _$namespace = new $("html", {
      namespace,
    });

    push(() => {
      if (_$namespace.empty) {
        _$namespace.push($.make("html"));
      }
    });

    return _$namespace;
  }

  namespace(namespace: string): $ {
    return new $(this, {
      namespace: [...this._namespace, namespace],
      previousElements: this.get(),
    });
  }

  static template(
    targetKey: string,
    parent?: $Selector | Document,
  ): Template | null {
    const $template = $.$template(targetKey, parent).parent;

    if ($template.empty) {
      return null;
    }

    return new Template($template, {
      defaultKey: targetKey,
      onTemplateInit: (element) => $.make(element).rk("template") ?? false,
    });
  }

  template(targetKey: string): Template | null {
    const $template = this.$template(targetKey).parent;

    if ($template.empty) {
      return null;
    }

    return new Template($template, {
      defaultKey: targetKey,
      onTemplateInit: (element) => this.make(element).rk("template") ?? false,
    });
  }

  templates(targetKey: string): Template[] {
    const $template = this.$template(targetKey).parent;

    return $template
      .map$(($element) => $element.template(targetKey))
      .filter((template) => template !== null);
  }

  adder(
    keyword: string,
    options: { defaultValue?: string[] } = {},
  ): $Attrs<Adder> {
    const { defaultValue } = options;

    const rawValue = this.rks(keyword);

    if (rawValue.length > 0) {
      try {
        return { rawValue, value: new Adder(...rawValue) };
      } catch (error) {
        if (!defaultValue || !(error instanceof SyntaxError)) {
          throw error;
        }
      }
    }

    return { rawValue, value: new Adder(...(defaultValue ?? [])) };
  }

  or(selector: $Selector): $ {
    return this.length > 0 ? this.make(this) : this.make(selector);
  }

  getBoundingRect() {
    return this.first?.getBoundingClientRect();
  }

  captureScroll(from: "top" | "bottom" = "top") {
    const $target = $.make(this.first);

    if (!$target.isValid) {
      return () => {};
    }

    const before = $target.getBoundingRect()![from];

    return () => {
      requestAnimationFrame(() => {
        const after = $target.getBoundingRect()![from];

        const diff = after - before;

        window.scrollBy({
          top: diff,
          behavior: "instant",
        });
      });
    };
  }

  static make(selector?: $Selector, parent?: $Selector | Document): $ {
    return new $(selector, {
      parent,
    });
  }

  make(selector?: $Selector, parent?: $Selector | Document): $ {
    return new $(selector, {
      namespace: this._namespace,
      parent,
      previousElements: this.get(),
    });
  }

  cns() {
    return $.make(this);
  }
}

export const $rk = $.$rk;
export const $rkk = $.$rkk;

export const $element = $.$element;
export const $wrap = $.$wrap;
export const $text = $.$text;
export const $button = $.$button;
export const $input = $.$input;
export const $form = $.$form;
export const $template = $.$template;
export const $data = $.$data;
